import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-void text-ink-100">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-100 mb-8"
        >
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="prose prose-invert prose-cyan max-w-none">
          <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-ink-500 mb-8">Last Updated: May 12, 2026</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
            <p className="text-ink-300 leading-relaxed mb-4">
              MnemonicAI is committed to protecting your privacy. This Privacy Policy explains how we collect, use, 
              disclose, and safeguard your information when you use our Service.
            </p>
            <p className="text-ink-300 leading-relaxed">
              <strong>Geographic Scope:</strong> This policy applies globally and complies with the Australian Privacy Act 1988, 
              EU GDPR, California CCPA, and other applicable data protection laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold mb-3">Account Information</h3>
            <ul className="list-disc list-inside text-ink-300 space-y-2 mb-4">
              <li>Name and email address</li>
              <li>Company name (optional)</li>
              <li>Password (encrypted)</li>
              <li>Payment information (processed by Stripe)</li>
            </ul>
            <h3 className="text-xl font-semibold mb-3">Usage Data</h3>
            <ul className="list-disc list-inside text-ink-300 space-y-2">
              <li>API requests and responses</li>
              <li>Service usage patterns</li>
              <li>Technical data (IP address, browser type, device info)</li>
              <li>Performance metrics</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside text-ink-300 space-y-2">
              <li><strong>To Provide the Service:</strong> Process reflections, store and retrieve memories, maintain your account</li>
              <li><strong>To Improve:</strong> Analyze usage patterns (anonymized), debug issues, optimize performance</li>
              <li><strong>To Communicate:</strong> Respond to inquiries, send service updates, marketing (with opt-out)</li>
              <li><strong>For Security:</strong> Detect fraud, prevent abuse, comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8 border-l-4 border-terminal pl-6">
            <h2 className="text-2xl font-bold mb-4">4. We DO NOT Sell Your Data</h2>
            <p className="text-terminal font-semibold mb-4">
              WE NEVER SELL YOUR PERSONAL INFORMATION TO THIRD PARTIES.
            </p>
            <p className="text-ink-300 leading-relaxed">
              We only share data with trusted service providers (OpenAI, Stripe, hosting providers) who are contractually 
              obligated to protect your data. We may disclose information if required by law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">5. Data Storage and Security</h2>
            <p className="text-ink-300 leading-relaxed mb-4">
              <strong>Cloud Tier:</strong> Data stored on AWS/Google Cloud servers in Australia with encryption at rest and in transit.
            </p>
            <p className="text-ink-300 leading-relaxed mb-4">
              <strong>Self-Hosted OSS:</strong> Lessons stored in our cloud database (processed through our API).
            </p>
            <p className="text-ink-300 leading-relaxed mb-4">
              <strong>Retention:</strong> Active accounts - indefinite retention; Closed accounts - 90 days then deleted.
            </p>
            <p className="text-ink-300 leading-relaxed">
              We implement industry-standard security measures including encryption, access controls, and regular security audits. 
              However, no method is 100% secure.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">6. Your Privacy Rights</h2>
            <p className="text-ink-300 leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-ink-300 space-y-2">
              <li><strong>Access:</strong> Request a copy of your data</li>
              <li><strong>Correction:</strong> Update inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your data</li>
              <li><strong>Portability:</strong> Export your data in machine-readable format</li>
              <li><strong>Object:</strong> Object to certain processing activities</li>
              <li><strong>Withdraw Consent:</strong> Opt-out of marketing communications</li>
            </ul>
            <p className="text-ink-300 leading-relaxed mt-4">
              To exercise these rights, email us at: <a href="mailto:mnemonicai.official@gmail.com" className="text-cyan-400 hover:underline">
                mnemonicai.official@gmail.com
              </a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">7. Cookies and Tracking</h2>
            <p className="text-ink-300 leading-relaxed">
              We use essential cookies for functionality, analytics cookies to understand usage (Google Analytics, anonymized), 
              and preference cookies to remember your settings. You can control cookies through your browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">8. Children's Privacy</h2>
            <p className="text-ink-300 leading-relaxed">
              Our Service is NOT intended for children under 18. We do not knowingly collect data from children. 
              If discovered, we will delete it immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9. Contact</h2>
            <p className="text-ink-300 leading-relaxed">
              Data Protection Inquiries:<br />
              Email: <a href="mailto:mnemonicai.official@gmail.com" className="text-cyan-400 hover:underline">
                mnemonicai.official@gmail.com
              </a><br />
              Address: Melbourne, Victoria, Australia
            </p>
          </section>

          <div className="border-t border-line-1 pt-8 mt-12">
            <p className="text-sm text-ink-500">
              <a 
                href="/legal/PRIVACY_POLICY.md" 
                target="_blank"
                className="text-cyan-400 hover:underline"
              >
                View Full Privacy Policy →
              </a>
            </p>
            <p className="text-xs text-ink-500 mt-4">
              Last Updated: May 12, 2026 | Version: 1.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
